// marketing.ts — Patch MCC-3a. Marketing import client (FUB contacts -> MCC
// profiles + cohort). The CSV is parsed in the browser (lib/csv.ts); this
// posts normalized contact chunks to the audited import endpoint.

import { request } from './request'

export interface ImportContactInput {
  fub_id?: string
  first_name?: string
  last_name?: string
  email?: string
  email2?: string
  phone?: string
  phone2?: string
  address_street?: string
  address_city?: string
  address_state?: string
  address_zip?: string
  lead_source?: string
  notes?: string
  // FUB Tags — the deterministic Step 0 segmentation key (past_client / agent /
  // sphere). Sent only when derive_segments is set.
  tags?: string
}

export interface ImportResult {
  ok: boolean
  cohort_id: string
  inserted: number
  updated: number
  skipped: number
  members_added: number
  tags_added: number
  segment_members_added: number
  segments: { past_client: number; agent: number; sphere: number }
  segment_cohort_ids?: { past_client?: string; agent?: string; sphere?: string }
  errors: string[]
  request_id?: string
}

// --- MCC-4a: Contacts workbench (cohort browser + per-profile CRM summary) ---
export interface CohortSummary {
  id: string
  name: string
  audience_type: string
  source: string | null
  status: string
  created_at: string
  member_count: number
}

export interface CohortMemberRow {
  id: string
  first_name: string | null
  last_name: string | null
  email_primary: string | null
  phone_mobile: string | null
  current_address_city: string | null
  lifecycle_stage: string
  member_since: string
  tags: string | null
}

export interface CohortMembersPage {
  ok: boolean
  cohort: { id: string; name: string }
  total: number
  limit: number
  offset: number
  members: CohortMemberRow[]
}

export interface ProfileCrmSummary {
  ok: boolean
  tags: Array<{ tag: string; source: string; created_at: string }>
  cohorts: Array<{ id: string; name: string; audience_type: string }>
}

// --- MCC-3c: Gmail past-client sweep ---
export interface SweepResult {
  ok: boolean
  messages_fetched: number
  contacts_found: number
  new_candidates: number
  already_profiled: number
  already_queued: number
  fetch_errors: number
  capped: boolean
}

export interface CandidateEvidence {
  message_count?: number
  sample_subjects?: string[]
  matched_queries?: string[]
  first_seen?: string | null
  last_seen?: string | null
  source_inbox?: string | null
}

export interface MarketingCandidate {
  id: string
  email: string | null
  name: string | null
  evidence: CandidateEvidence | null
  reasoning: string | null
  created_at: string
}

export const marketingApi = {
  importContacts: (body: {
    cohort_name: string
    source?: string
    audience_type?: string
    derive_segments?: boolean
    contacts: ImportContactInput[]
  }) =>
    request<ImportResult>('/api/admin/marketing-contacts-import', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  runSweep: (body?: { before?: string; after?: string }) =>
    request<SweepResult>('/api/admin/marketing-sweep/scan', {
      method: 'POST',
      body: JSON.stringify(body ?? {}),
    }),

  listCandidates: () =>
    request<{ ok: boolean; candidates: MarketingCandidate[] }>(
      '/api/admin/marketing-candidates',
    ),

  approveCandidate: (
    id: string,
    target: 'past_client' | 'agent' | 'partner' = 'past_client',
  ) =>
    request<{ ok: boolean; profile_id: string; created_profile: boolean; member_added: boolean }>(
      `/api/admin/marketing-candidates/${id}/approve`,
      { method: 'POST', body: JSON.stringify({ target }) },
    ),

  rejectCandidate: (id: string) =>
    request<{ ok: boolean }>(`/api/admin/marketing-candidates/${id}/reject`, {
      method: 'POST',
    }),

  // --- MCC-4a: Contacts workbench ---
  listCohorts: () =>
    request<{ ok: boolean; cohorts: CohortSummary[] }>('/api/admin/cohorts'),

  cohortMembers: (
    cohortId: string,
    opts?: { search?: string; limit?: number; offset?: number },
  ) => {
    const params = new URLSearchParams()
    if (opts?.search) params.set('search', opts.search)
    if (opts?.limit) params.set('limit', String(opts.limit))
    if (opts?.offset) params.set('offset', String(opts.offset))
    const qs = params.toString()
    return request<CohortMembersPage>(
      `/api/admin/cohorts/${cohortId}/members${qs ? `?${qs}` : ''}`,
    )
  },

  removeCohortMember: (cohortId: string, profileId: string) =>
    request<{ ok: boolean; removed: boolean }>(
      `/api/admin/cohorts/${cohortId}/members/${profileId}`,
      { method: 'DELETE' },
    ),

  profileCrm: (profileId: string) =>
    request<ProfileCrmSummary>(`/api/admin/profiles/${profileId}/crm`),
}
